import CurrentPlan from "../models/CurrentPlan";
import { toShortDateWithoutTime } from "../../../../core/Global";
import Icon from "../../../../components/base/Icon";
import "./CurrentPlanCard.css";
import "./PlanChange.css";
import "./SignedInDrawer.css";
import { trackGrowth } from "../../../../components/growth/GrowthSignal";


// One labelled fact in the footer -- when it started, when it renews.
function PlanFact(props: { icon: any, label: string, value?: string, danger?: boolean }) {
    return (
        <div>
            <div className="current-plan-fact-label">
                <Icon icon={props.icon} size={12} />
                {props.label}
            </div>
            <div className={"current-plan-fact-value" + (props.danger ? " is-danger" : "")}>
                {props.value}
            </div>
        </div>
    )
}


// Only an old subscription wears a status.
//
// A plan has none worth showing: it is either the plan the customer is on, or -- when a paid one
// is not renewed -- it becomes the Free plan, which is itself a plan they are on. There is no
// expired, lapsed or inactive state to warn about, so an "Active" badge would say nothing. The
// old subscriptions do have those states, and they keep the badge until they are gone.
//
// The badge colour is decided by what the customer would have to do about it: nothing (green),
// something soon (amber), or something now (red).
function StatusBadge(props: { plan: CurrentPlan }) {

    const p = props.plan;

    const color =
        p.paymentIssue ? "warning"
            : p.isSuspended ? "danger"
                : p.isCanceled && p.isActive ? "warning"
                    : p.isActive ? "success"
                        : "danger";

    return (
        <span className={"badge badge-light-" + color + " fw-bold px-3 py-2"}>
            <Icon icon="circle" type="solid" className="me-2" size={8} />
            {p.statusText}
        </span>
    )
}


export default function CurrentPlanCard(props: {
    plan: CurrentPlan,
    onChangePlan?: () => void,
    onCancel?: () => void,
    onFixPayment?: () => void,
    onUndoCancel?: () => void,
    // A scheduled downgrade or pause, taken back -- the banner's "Cancel this change".
    onCancelChange?: () => void,
    // A paused account brought back.
    onResume?: () => void,
    // Who is holding the user slots right now, from the "Concurrent users" tile.
    onShowSignedIn?: () => void
}) {

    const plan = props.plan;

    // Allowances of zero are still worth showing: "Messages — None, $0.03 each after" is the
    // whole story of the Free plan, and hiding the row would leave the customer guessing.
    const allowances = plan.allowances ?? [];

    // Cancelled and still running. It is a state of its own, not a variation on active: the plan
    // works exactly as before, and it has an end date.
    const ending = !!plan.isPlan && !plan.isFree && !!plan.isCanceled && !!plan.isActive;

    // Paused: read-only until it is resumed or runs out.
    const onHold = !!plan.isOnHold;

    // A downgrade or a pause on its way. It waits for the end of the period, and until it has
    // been charged for -- a day or so before -- it can be taken back.
    const pending = plan.pendingKind;

    return (
        <div className="current-plan">

            {
                /* At the top, not the bottom. This is the first thing true about the plan, and
                   below the allowances it was a footnote to a card that otherwise read as
                   business as usual. */
                ending &&
                <div className="current-plan-ending">
                    <Icon icon="circle-exclamation" type="solid" size={18} />
                    <div>
                        <div className="current-plan-ending-title">
                            This plan ends on {toShortDateWithoutTime(plan.periodEnd)}
                        </div>
                        <div className="current-plan-ending-text">
                            Nothing changes until then — every allowance, number and user stays as
                            it is, and you will not be charged again. On that date the account
                            moves to the Free plan.
                        </div>
                    </div>
                </div>
            }

            {
                onHold &&
                <div className="current-plan-change is-hold">
                    <Icon icon="pause" type="solid" size={18} />
                    <div className="current-plan-change-body">
                        <div className="current-plan-change-title">
                            Your account is paused until {toShortDateWithoutTime(plan.holdEndsAt)}
                        </div>
                        <div className="current-plan-change-text">
                            It is read-only: everyone can sign in and look, but nothing can be changed or
                            sent. Your data and phone numbers are kept. It costs ${plan.holdPrice} a month
                            while it is paused. If it is not resumed by {toShortDateWithoutTime(plan.holdEndsAt)},
                            the account moves to the Free plan.
                        </div>
                    </div>
                    {
                        props.onResume &&
                        <button type="button" className="btn btn-sm btn-primary flex-shrink-0"
                            onClick={e => { e.preventDefault(); props.onResume!(); }}>
                            <Icon icon="play" type="solid" className="me-2" size={11} />
                            Resume
                        </button>
                    }
                </div>
            }

            {
                !onHold && pending &&
                <div className={"current-plan-change" + (pending === "hold" ? " is-hold" : "")}>
                    <Icon icon={pending === "hold" ? "pause" : pending === "upgrade" ? "arrow-up-right-dots" : "arrow-down-right"} type="solid" size={18} />
                    <div className="current-plan-change-body">
                        <div className="current-plan-change-title">
                            {
                                pending === "hold"
                                    ? `Your account will be paused on ${toShortDateWithoutTime(plan.pendingEffectiveAt)}`
                                    : pending === "upgrade"
                                        ? `Your plan upgrades to ${plan.pendingPlanName} on ${toShortDateWithoutTime(plan.pendingEffectiveAt)}`
                                        : `Your plan changes to ${plan.pendingPlanName} on ${toShortDateWithoutTime(plan.pendingEffectiveAt)}`
                            }
                        </div>
                        <div className="current-plan-change-text">
                            {
                                pending === "hold"
                                    ? `For ${plan.pendingHoldMonths} month${plan.pendingHoldMonths === 1 ? "" : "s"}, at $${plan.pendingPrice} a month. Nothing changes until then.`
                                    : (plan.pendingPrice ?? 0) <= 0
                                        ? `You keep everything ${plan.displayName ?? plan.planName} includes until then. From that day you are on the Free plan and nothing more is charged.`
                                        : `You keep everything ${plan.displayName ?? plan.planName} includes until then. From that day it is $${plan.pendingPrice} ${plan.isAnnual ? "a year" : "a month"}, and your billing date stays the same.`
                            }
                            {
                                plan.pendingPaid &&
                                " It has already been paid for, so it can no longer be cancelled here."
                            }
                        </div>
                    </div>
                    {
                        !plan.pendingPaid && props.onCancelChange &&
                        <button type="button" className="btn btn-sm btn-light-primary flex-shrink-0"
                            onClick={e => { e.preventDefault(); props.onCancelChange!(); }}>
                            Cancel this change
                        </button>
                    }
                </div>
            }

            {
                /* Rinvox changed this plan and sent the change on to existing subscribers. */
                !pending && plan.catalogChangeAt && (plan.catalogChangeLines?.length ?? 0) > 0 &&
                <div className="current-plan-change">
                    <Icon icon="arrows-rotate" type="solid" size={18} />
                    <div className="current-plan-change-body">
                        <div className="current-plan-change-title">
                            {`Your ${plan.displayName ?? plan.planName} plan changes on ${toShortDateWithoutTime(plan.catalogChangeAt)}`}
                        </div>
                        <div className="current-plan-change-text">
                            {
                                plan.catalogChangeLines!.map((l, i) =>
                                    <div key={i}>
                                        {l.label}: {l.from}
                                        <Icon icon="arrow-right" type="solid" size={9} className="mx-2 text-gray-500" />
                                        <b>{l.to}</b>
                                    </div>
                                )
                            }
                            <div className="mt-1">Nothing changes before then.</div>
                        </div>
                    </div>
                </div>
            }

            <div className="current-plan-head">
                <div>
                    <h2 className={"current-plan-name" + (plan.isFree ? " is-lead" : "")}>
                        {plan.displayName ?? plan.planName}
                        {plan.isLegacy && <StatusBadge plan={plan} />}
                        {
                            ending &&
                            <span className="badge badge-light-warning fw-bold px-3 py-2">
                                Cancelled
                            </span>
                        }
                        {
                            onHold &&
                            <span className="badge badge-light-warning fw-bold px-3 py-2">Paused</span>
                        }
                        {
                            plan.isAnnual &&
                            <span className="badge badge-light-primary fw-bold px-3 py-2">Annual</span>
                        }
                    </h2>
                    {
                        /* Nothing is owed on a free plan, so there is no figure to put here. The
                           name already says what it costs, and "$0" only invites the question of
                           what it is $0 of. */
                        !plan.isFree &&
                        <div className="current-plan-price">
                            <span className="current-plan-amount">{plan.priceText}</span>
                            {
                                plan.periodText &&
                                <span className="current-plan-period">{plan.periodText}</span>
                            }
                        </div>
                    }
                </div>

                <div className="current-plan-actions">
                    {
                        plan.paymentIssue && props.onFixPayment &&
                        <button type="button" className="btn btn-sm btn-primary"
                            onClick={e => { e.preventDefault(); props.onFixPayment!(); }}>
                            <Icon icon="triangle-exclamation" type="solid" className="me-2" />
                            Fix Payment
                        </button>
                    }
                    {
                        plan.isCanceled && plan.isActive && props.onUndoCancel &&
                        <button type="button" className="btn btn-sm btn-outline btn-outline-primary btn-active-light-primary"
                            onClick={e => { e.preventDefault(); props.onUndoCancel!(); }}>
                            Undo Cancellation
                        </button>
                    }
                    {
                        /* Not offered while the plan is ending. Changing plan from here would have
                           to decide what happens to the cancellation, and the answer is the button
                           beside it: undo first, then change. */
                        props.onChangePlan && !ending && !onHold &&
                        <button type="button"
                            className={"btn btn-sm " + (plan.isFree ? "btn-primary" : "btn-light-primary")}
                            onClick={e => {
                                e.preventDefault();
                                if (plan.isFree) trackGrowth("upgrade_clicked", "plan", "subscription page");
                                props.onChangePlan!();
                            }}>
                            <Icon icon="arrow-up-right-dots" type="solid" className="me-2" />
                            {
                                /* On Free there is only one direction to go, and naming it is the
                                   whole point of the button -- "Change Plan" reads as an admin
                                   task, "Upgrade Plan" as an offer. A paid plan can move either
                                   way, so it keeps the neutral word. */
                                plan.isFree ? "Upgrade Plan" : "Change Plan"
                            }
                        </button>
                    }
                    {
                        /* On one of the new plans, leaving is "Downgrade to Free" in Change Plan -- the
                           same scheduled change as any other downgrade -- so there is no second button
                           for it. The old subscription still cancels from here. */
                        plan.isActive && !plan.isCanceled && !plan.isFree && !plan.isPlan && props.onCancel &&
                        <button type="button" className="btn btn-sm btn-outline btn-outline-danger btn-active-light-danger"
                            onClick={e => { e.preventDefault(); props.onCancel!(); }}>
                            {
                                /* "Unsubscribe" on a plan, because that is what it does: the plan
                                   runs to the end of the period and then stops. The old wording
                                   belongs to the old subscription, which cancels differently. */
                                plan.isPlan ? "Unsubscribe" : "Cancel Subscription"
                            }
                        </button>
                    }
                </div>
            </div>

            {
                allowances.length > 0 &&
                <div className="current-plan-allowances">
                    <div className="current-plan-section-title">Your plan this period</div>
                    <div className="current-plan-grid">
                        {
                            allowances.map((a, key) =>
                                <div className="current-plan-allowance" key={key}>
                                    <div className="current-plan-allowance-label">
                                        <Icon icon={a.icon} size={13} />
                                        <span className="current-plan-allowance-name" title={a.label}>{a.label}</span>
                                        {
                                            /* Top right, beside the label, so it costs the tile no extra line. */
                                            a.key === "users" && a.isOccupancy && props.onShowSignedIn &&
                                            <button type="button" className="current-plan-allowance-link" title="See who is signed in"
                                                onClick={e => { e.preventDefault(); props.onShowSignedIn!(); }}>
                                                <Icon icon="eye" type="solid" size={11} />
                                                <span>Online</span>
                                            </button>
                                        }
                                    </div>
                                    <div className={"current-plan-allowance-value" + (a.included <= 0 ? " is-none" : "")}>
                                        {
                                            /* People signed in now, numbers on the account: what is
                                               sitting under the ceiling, read as "3 of 8". */
                                            a.isOccupancy ?
                                                <>
                                                    {a.used ?? 0}
                                                    <span className="current-plan-allowance-suffix">of {a.includedText}</span>
                                                </>
                                                :
                                                <>
                                        {
                                            /* What is left, not what was bought. The included
                                               figure never moves, so leading with it answered a
                                               question nobody had; it is still on the line below,
                                               inside "2 of 1,500 used". A ceiling like concurrent
                                               users has nothing to count down, so it keeps saying
                                               what it is. */
                                            a.isMetered && a.included > 0 ? a.remainingText : a.includedText
                                        }
                                        {
                                            a.isMetered && a.included > 0 &&
                                            <span className="current-plan-allowance-suffix">left</span>
                                        }
                                                </>
                                        }
                                    </div>
                                    {
                                        /* What is left, for the allowances that get spent. The
                                           number the plan includes never changes, so on its own it
                                           never answered the question somebody opens this page
                                           with -- how much have I got left. */
                                        (a.isMetered || a.isOccupancy) && a.included > 0 &&
                                        <>
                                            <div className="current-plan-meter">
                                                <div className={"current-plan-meter-fill" + (a.isOver ? " is-over" : "")}
                                                    style={{ width: (a.usedPercent ?? 0) + "%" }} />
                                            </div>
                                            <div className={"current-plan-allowance-used" + (a.isOver ? " is-over" : "")}>
                                                {a.usedText}
                                            </div>
                                        </>
                                    }
                                    {
                                        a.overageText &&
                                        <div className="current-plan-allowance-rate">{a.overageText}</div>
                                    }
                                </div>
                            )
                        }
                    </div>
                </div>
            }

            {
                /* An old subscription sells seats and nothing else, so that is all there is to
                   say about what it includes. */
                plan.isLegacy &&
                <div className="current-plan-allowances">
                    <div className="current-plan-section-title">Included in your subscription</div>
                    <div className="current-plan-grid">
                        <div className="current-plan-allowance">
                            <div className="current-plan-allowance-label">
                                <Icon icon="headset" size={13} />
                                Seats
                            </div>
                            <div className="current-plan-allowance-value">{plan.seatsIncluded}</div>
                        </div>
                    </div>
                </div>
            }

            <div className="current-plan-foot">
                {
                    /* A free plan has no dates worth showing: it does not run out, does not renew
                       and cannot lapse, so a start and an end only invite the question of what
                       happens on that day. The period underneath is real -- it is the window the
                       allowances are counted in -- but that is a meter, not a term. */
                    !plan.isFree && onHold &&
                    <>
                        <PlanFact icon="pause" label="Paused since"
                            value={toShortDateWithoutTime(plan.holdStartedAt)} />
                        <PlanFact icon="calendar-clock" label="Paused until"
                            value={toShortDateWithoutTime(plan.holdEndsAt)} />
                    </>
                }
                {
                    !plan.isFree && !onHold &&
                    <>
                        <PlanFact icon="calendar-day" label="Started"
                            value={toShortDateWithoutTime(plan.startedAt)} />
                        <PlanFact
                            icon={plan.isActive && !plan.isCanceled ? "arrows-rotate" : "calendar-clock"}
                            label={
                                plan.paymentIssue ? "Payment due"
                                    : plan.isCanceled && plan.isActive ? "Cancels on"
                                        : plan.isActive ? "Next renewal"
                                            : "Ended on"
                            }
                            value={toShortDateWithoutTime(plan.periodEnd)}
                            danger={plan.isLegacy && (plan.paymentIssue || !plan.isActive)} />
                    </>
                }
                {
                    /* Only worth a line when there is headroom to buy: on a plan whose ceiling is
                       what it already includes, this repeats the Concurrent users box above. */
                    plan.isPlan && (plan.maxUsers ?? 0) > (plan.includedUsers ?? 0) &&
                    <PlanFact icon="user-group" label="Can grow to"
                        value={plan.maxUsers + " concurrent users"} />
                }
                {
                    plan.supportTier &&
                    <PlanFact icon="life-ring" label="Support" value={plan.supportTier} />
                }
            </div>

        </div>
    )
}
